/* =====================================================
   BOOST ARTWORK PICKER — assets/boost-artwork-picker.js

   Drives the accordion-based .boost-artwork-picker UI:
     - Toggles top-level rows (no_artwork / sample_only / add_artwork)
     - Toggles nested decoration method accordion (one open at a time)
     - Updates the "Add logo or artwork" closed-row summary text
     - Intercepts the buy-button submit and AJAX-adds the garment +
       carrier products as a single _product_key group
     - Adds the artwork setup fee once per cart (when needed)
     - Persists artwork state across size/variant changes

   ============================================================
   BOOST CUSTOMISATION 2026-05-21 (v2.3.0) — MULTI-METHOD REWRITE
   ============================================================
   This is the v2.3.0 multi-method rewrite. The picker previously
   allowed exactly ONE artwork method per garment. It now allows ANY
   combination across the four methods (Embroidery, Screen Print,
   Supacolour, Heat Press), each one-or-none. The team Numbers and
   Names fieldsets now live INSIDE the Heat Press method body (moved
   from a standalone .bap-team-pane) and remain independently
   selectable. See the v2.3.0 design doc for full rationale.

   WHAT CHANGED FROM THE SINGLE-METHOD MODEL:
     OLD state:  method (one string), pitchId/pitchTitle/... (scalars)
     NEW state:  methods = { embroidery, screen_print, supacolour,
                 heat_press }  — each null OR
                 { pitchId, pitchTitle, pitchHandle, pitchPrice }
     OLD:        method toggle = both "open this accordion" AND
                 "select this method" (conflated)
     NEW:        openMethod (UI-only, single-expansion accordion) is
                 DECOUPLED from per-method selection. Selecting a pitch
                 in one method never clears another method.

   SELECTION RULES (v2.3.0):
     - Each of the four methods: one pitch or none. Radios stay.
     - A method header shows the Boost Red selected outline when that
       method has a pitch selected, regardless of whether its
       accordion is open.
     - Heat Press header additionally shows selected when ANY team
       item is chosen (team lives in the Heat Press body). (Q1)
     - Team fieldsets: each slot one option or none, independent.
     - A customer may select team items WITHOUT a Heat Press pitch. (Q1)

   CART-ADD (v2.3.0):
     One decoration carrier line per selected method (loop), plus one
     carrier line per selected team slot, all sharing one _product_key.

     The cart can therefore hold up to 4 method carriers + up to 4
     team carriers + the garment, all under one _product_key.

   MERGE (v2.3.0):
     Merge-into-existing-group is now restricted to the SIMPLEST case:
     a garment with EXACTLY ONE artwork method and NO team items.
     Any multi-method selection OR any team selection forces a FRESH
     ADD (no merge), consistent with how team already behaved pre-2.3.0.
     This avoids fragile multi-line signature matching.

   STATE PERSISTENCE (v2.3.0):
     The store is password-protected with a single user, so there is
     no in-flight customer state to preserve across the deploy. Old
     single-method saved state (state.method present) is DISCARDED on
     load rather than migrated. (Q5)

   CHANGE LOG (pre-v2.3.0 entries retained for history):
     2026-05-15 — UPD (round 2.1): removed artworkRequired read.
     2026-05-15 — UPD (round 2): decoupled selected vs open at row level.
     2026-05-15 — REWRITE: accordion UI per approved v5 mockup.
     2026-05-14 — Earlier fixes (submit intercept, sections, etc.)
   ============================================================ */

(function () {
  'use strict';

  /* ---------- Helpers ---------- */

  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function generateProductKey() {
    return 'pk-' + Math.random().toString(16).slice(2, 8);
  }

  /* ---------- State persistence across variant swaps ----------
   *
   * When the customer changes size, the theme re-fetches and replaces
   * .f8pr-buy-button (which contains the artwork picker). The picker HTML
   * is rebuilt from Liquid, resetting all selections. We save state to
   * sessionStorage keyed by product ID so we can restore it on re-init.
   */

  function saveState(productId, state) {
    try {
      sessionStorage.setItem('bap-state-' + productId, JSON.stringify(state));
    } catch (e) {}
  }

  function loadState(productId) {
    try {
      var raw = sessionStorage.getItem('bap-state-' + productId);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  /* ---------- Pretty-name maps ---------- */

  var METHOD_LABELS = {
    embroidery: 'Embroidery',
    screen_print: 'Screen Print',
    // BOOST CUSTOMISATION 2026-06-03 (v2.4.0): Heat Press relabelled Standard DTF,
    // Supacolour relabelled Supacolour Premium DTF. Method KEYS are unchanged so
    // carriers, fees and saved state are unaffected.
    heat_press: 'Standard DTF',
    supacolour: 'Supacolour Premium DTF',
    // BOOST CUSTOMISATION 2026-07-11 (Bundle 2 - Headwear): headwear method labels.
    // 'embroidery' is shared with the apparel key (same label, same fee handle).
    // A PDP is either apparel or headwear, so the two sets never render together.
    plastisol: 'Plastisol',
    power_print: 'Power Print',
    rubber_print: 'Rubber Print',
    patch_standard: 'Standard Patch',
    patch_premium: 'Premium Patch'
  };

  // BOOST CUSTOMISATION 2026-06-03 (v2.4.0):
  // Reordered to the flattened picker order (Embroidery, Supacolour Premium DTF,
  // Standard DTF, then Screen Print). Names and Numbers (team slots) sits between
  // Standard DTF and Screen Print and is handled via TEAM_ORDER / SUMMARY_SEQUENCE.
  // BOOST CUSTOMISATION 2026-07-11 (Bundle 2 - Headwear): headwear method keys
  // appended. A PDP renders apparel OR headwear methods (never both), so the
  // unused keys stay null and are skipped by selectedMethodCount()/summary.
  // For a headwear PDP the present methods iterate in the intended order:
  // embroidery, plastisol, power_print, rubber_print, patch_standard, patch_premium.
  var METHOD_ORDER = ['embroidery', 'supacolour', 'heat_press', 'screen_print', 'plastisol', 'power_print', 'rubber_print', 'patch_standard', 'patch_premium'];

  // BOOST CUSTOMISATION 2026-06-03 (v2.4.0): visual order for the closed-row
  // summary line. The 'names_numbers' token expands to the team slots (TEAM_ORDER)
  // so Names and Numbers appears between Standard DTF and Screen Print.
  var SUMMARY_SEQUENCE = ['embroidery', 'supacolour', 'heat_press', 'names_numbers', 'screen_print', 'plastisol', 'power_print', 'rubber_print', 'patch_standard', 'patch_premium'];

  // BOOST CUSTOMISATION 2026-05-21 (v2.3.0):
  // Canonical team-slot order for cart-add and display:
  // Team Name → Player Name → Front Number → Back Number.
  var TEAM_ORDER = ['team_name', 'player_name', 'front_number', 'back_number'];

  // Team slot display labels (for the summary line).
  // BOOST CUSTOMISATION 2026-05-21 (v2.3.0): shortened to match the picker
  // legends ("Team Name" / "Player Name", no Front/Back suffix).
  var TEAM_SLOT_LABELS = {
    team_name: 'Team Name',
    player_name: 'Player Name',
    front_number: 'Front Number',
    back_number: 'Back Number'
  };

  /* ---------- Picker instance ---------- */

  function initPicker(pickerEl) {
    if (pickerEl.dataset.bapInited === 'true') return;
    pickerEl.dataset.bapInited = 'true';

    // BOOST CUSTOMISATION 2026-05-17 (v2.2):
    // Sort pitch options within each pitch-list by data-pitch-menu-order.
    // Set via the picker-menu-N tag in Shopify admin. Options without a
    // tag fall to the end (default order 99). Runs once at init.
    // NOTE (v2.3.0): this also covers the team groups now nested in Heat
    // Press. Their options live in .bap-team-card (a plain div, replacing
    // the earlier fieldset), so we target .bap-team-card here to append
    // sorted options back into the correct white-card container.
    Array.prototype.slice.call(pickerEl.querySelectorAll('.bap-pitch-list, .bap-team-card')).forEach(function (list) {
      var options = Array.prototype.slice.call(list.querySelectorAll('.bap-pitch-option'));
      if (options.length < 2) return;
      options.forEach(function (opt) {
        var input = opt.querySelector('input[data-pitch-menu-order]');
        var ord = input ? parseInt(input.getAttribute('data-pitch-menu-order'), 10) : 99;
        opt.__bapMenuOrder = isNaN(ord) ? 99 : ord;
      });
      // Keep any "None" option (empty value) first, in both method
      // pitch-lists and team groups. Real options then sort by menu order.
      options.sort(function (a, b) {
        var aInput = a.querySelector('input');
        var bInput = b.querySelector('input');
        var aNone = aInput && !aInput.value;
        var bNone = bInput && !bInput.value;
        if (aNone && !bNone) return -1;
        if (bNone && !aNone) return 1;
        if (a.__bapMenuOrder !== b.__bapMenuOrder) return a.__bapMenuOrder - b.__bapMenuOrder;
        return 0;
      });
      options.forEach(function (opt) { list.appendChild(opt); });
    });

    var productId = pickerEl.dataset.productId || '';

    // Read static refs from the picker element's data attributes.
    var refs = {
      artworkCarrierId: pickerEl.dataset.artworkCarrierId,
      artworkSetupFeeId: pickerEl.dataset.artworkSetupFeeId,
      asColourSampleId: pickerEl.dataset.asColourSampleId,
      // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): generic sample
      // acknowledgement for non-AS-Colour sample-only adds. Resolved by handle
      // 'confirmation-of-sample-product-order' in boost-artwork-picker.liquid.
      genericSampleId: pickerEl.dataset.genericSampleId,
      vendor: pickerEl.dataset.productVendor,
      // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): read from the corrected
      // data-sample-policy-mandatory attribute (was data-sample-policy, which
      // was not camelCase-matching samplePolicyMandatory in JS, so the AS Colour
      // carrier was silently never written for mandatory-policy products).
      samplePolicyMandatory: pickerEl.dataset.samplePolicyMandatory === 'true',
      // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): product title for the
      // "For garment: <title (colour / size)>" visible property on carriers.
      productTitle: pickerEl.dataset.productTitle || '',
      renderTeamPane: pickerEl.dataset.renderTeamPane === 'true',
      defaultChoice: pickerEl.dataset.defaultChoice || 'no_artwork'
    };

    // ── Initialise state ──
    // BOOST CUSTOMISATION 2026-05-21 (v2.3.0): per-method selection map.
    function freshState() {
      return {
        schema: 3,            // v2.3.0 state schema marker
        choice: refs.defaultChoice,
        isOpen: false,
        openMethod: null,     // UI-only: which method accordion is expanded
        methods: {            // selection per method (decoupled from openMethod)
          embroidery: null,
          screen_print: null,
          supacolour: null,
          heat_press: null,
          // BOOST CUSTOMISATION 2026-07-11 (Bundle 2 - Headwear):
          plastisol: null,
          power_print: null,
          rubber_print: null,
          patch_standard: null,
          patch_premium: null
        },
        team: {
          front_number: null,
          back_number: null,
          team_name: null,
          player_name: null
        }
      };
    }

    var state = freshState();

    // Serialises overlapping add-to-cart submissions (prevents races when
    // the buy button is clicked rapidly).
    var submissionChain = Promise.resolve();

    // Restore saved state on variant swap.
    // BOOST CUSTOMISATION 2026-05-21 (v2.3.0): DISCARD any pre-v2.3.0
    // single-method state (no schema marker / has old `method` key).
    // Q5: store is password-protected single-user, no migration needed.
    var saved = productId ? loadState(productId) : null;
    if (saved && saved.schema === 3 && saved.methods) {
      // Defensive: ensure the saved choice is still valid for this product.
      var validChoices = ['no_artwork', 'sample_only', 'add_artwork'];
      if (validChoices.indexOf(saved.choice) === -1) {
        saved.choice = refs.defaultChoice;
      }
      if (saved.choice === 'no_artwork' && !pickerEl.querySelector('[data-choice="no_artwork"]')) {
        saved.choice = refs.defaultChoice;
      }
      if (typeof saved.isOpen !== 'boolean') saved.isOpen = false;
      // Backfill any missing method keys (defensive against schema drift).
      ['embroidery', 'screen_print', 'supacolour', 'heat_press', 'plastisol', 'power_print', 'rubber_print', 'patch_standard', 'patch_premium'].forEach(function (m) {
        if (typeof saved.methods[m] === 'undefined') saved.methods[m] = null;
      });
      ['front_number', 'back_number', 'team_name', 'player_name'].forEach(function (t) {
        if (!saved.team || typeof saved.team[t] === 'undefined') {
          saved.team = saved.team || {};
          saved.team[t] = null;
        }
      });
      state = saved;
    }

    /* ---------- Selection helpers ---------- */

    // Count how many methods currently have a pitch selected.
    function selectedMethodCount() {
      var n = 0;
      METHOD_ORDER.forEach(function (m) {
        if (state.methods[m] && state.methods[m].pitchId) n++;
      });
      return n;
    }

    // Any team slot filled?
    function anyTeamSelected() {
      return TEAM_ORDER.some(function (t) {
        return state.team[t] && state.team[t].variantId;
      });
    }

    // BOOST CUSTOMISATION 2026-07-02 (WS2 fix, T-0702-313): "Add logo or artwork"
    // counts as decorated only when at least one method pitch OR one team item is
    // selected. An empty "Add logo or artwork" adds the garment undecorated (no
    // carriers, no _product_key, no fees), i.e. it behaves like "No logo or artwork".
    function isEffectivelyDecorated() {
      return state.choice === 'add_artwork' && (selectedMethodCount() > 0 || anyTeamSelected());
    }

    // Is a given method "selected" for header styling purposes?
    // BOOST CUSTOMISATION 2026-06-03 (v2.4.0): the team slots now live under the
    // Names and Numbers top-level method, so that header is "selected" when any
    // team item is chosen (previously this was attached to Heat Press).
    function methodIsSelected(method) {
      var has = !!(state.methods[method] && state.methods[method].pitchId);
      if (method === 'names_numbers' && anyTeamSelected()) return true;
      return has;
    }

    /* ---------- Rendering ---------- */

    function renderRows() {
      $$('.bap-row', pickerEl).forEach(function (row) {
        var choice = row.getAttribute('data-choice');
        var isSelected = state.choice === choice;
        var hasBody = !!row.querySelector('.bap-row-content');
        var isOpen = isSelected && state.isOpen && hasBody;

        row.classList.toggle('is-selected', isSelected);
        row.classList.toggle('is-open', isOpen);

        var header = row.querySelector('.bap-row-header');
        if (header) {
          if (!hasBody) {
            header.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          } else {
            header.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
          }
        }
        var body = row.querySelector('.bap-row-content');
        if (body) body.hidden = !isOpen;
      });
    }

    function renderMethods() {
      // BOOST CUSTOMISATION 2026-05-21 (v2.3.0):
      // Accordion open state (openMethod, single-expansion) is now
      // DECOUPLED from selection. A method body is open only when it is
      // the openMethod. A method header carries .is-selected independently
      // whenever that method has a selection (or, for Heat Press, any
      // team item) — see methodIsSelected().
      $$('.bap-method', pickerEl).forEach(function (m) {
        var name = m.getAttribute('data-method');
        var isOpen = state.openMethod === name;
        var isSel = methodIsSelected(name);

        m.classList.toggle('is-open', isOpen);
        m.classList.toggle('is-selected', isSel);

        var header = m.querySelector('.bap-method-header');
        if (header) header.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

        var content = m.querySelector('.bap-method-content');
        if (content) content.hidden = !isOpen;
      });
    }

    function renderPitchSelection() {
      // BOOST CUSTOMISATION 2026-05-21 (v2.3.0): each method's pitch
      // radios reflect that method's own selection, independent of others.
      // When a method has no selection, its "None" radio (empty value) is
      // checked so the radio group always has exactly one checked option.
      $$('.bap-method', pickerEl).forEach(function (methodEl) {
        var method = methodEl.getAttribute('data-method');
        var sel = state.methods[method];
        var selId = sel && sel.pitchId ? sel.pitchId : null;
        $$('.bap-pitch-option input[data-bap-pitch="true"]', methodEl).forEach(function (input) {
          var label = input.closest('.bap-pitch-option');
          var isNone = !input.value;
          if (isNone) {
            // The "None" radio: checked only when this method has no pitch.
            input.checked = !selId;
            if (label) label.classList.remove('is-selected'); // None is never "selected" styling
          } else {
            var isMatch = selId && parseInt(input.value, 10) === selId;
            if (isMatch) {
              input.checked = true;
              if (label) label.classList.add('is-selected');
            } else {
              if (label) label.classList.remove('is-selected');
              if (input.checked && !isMatch) input.checked = false;
            }
          }
        });
      });
    }

    function renderTeamSelection() {
      // BOOST CUSTOMISATION 2026-06-03 (v2.4.0): team fieldsets now live inside
      // the Names and Numbers method body. Each radio
      // group reflects its own slot's selection.
      $$('.bap-team-card .bap-pitch-option', pickerEl).forEach(function (label) {
        var input = label.querySelector('input[type="radio"]');
        if (!input) return;
        var slot = input.getAttribute('data-team-method');
        var sel = slot ? state.team[slot] : null;
        var selId = sel && sel.variantId ? sel.variantId : null;
        var isMatch = !!input.value && selId && parseInt(input.value, 10) === selId;
        var isNoneChecked = !input.value && !selId;
        if (isMatch) {
          input.checked = true;
          label.classList.add('is-selected');
        } else if (isNoneChecked) {
          input.checked = true;       // the "None" radio
          label.classList.remove('is-selected');
        } else {
          if (input.checked && !isMatch && input.value) input.checked = false;
          label.classList.remove('is-selected');
        }
      });
    }

    function renderSummary() {
      // BOOST CUSTOMISATION 2026-05-21 (v2.3.0) — Option B (Q3):
      // The single-method "Decoration: <pitch>" line is replaced by a
      // multi-line list, one entry per selected method (and team items),
      // each shown in context. Rendered into [data-bap-summary] as a
      // small stacked list. If nothing selected, summary is empty.
      var summaryEl = pickerEl.querySelector('[data-bap-summary]');
      if (!summaryEl) return;

      if (state.choice !== 'add_artwork') {
        summaryEl.textContent = '';
        summaryEl.classList.remove('has-summary');
        return;
      }

      var parts = [];
      // BOOST CUSTOMISATION 2026-06-03 (v2.4.0): build the summary in the
      // flattened picker order, with Names and Numbers (team slots) shown
      // between Standard DTF and Screen Print.
      SUMMARY_SEQUENCE.forEach(function (item) {
        if (item === 'names_numbers') {
          TEAM_ORDER.forEach(function (t) {
            var ts = state.team[t];
            if (ts && ts.variantId) {
              parts.push(TEAM_SLOT_LABELS[t] + ': ' + ts.title);
            }
          });
        } else {
          var sel = state.methods[item];
          if (sel && sel.pitchId) {
            parts.push(METHOD_LABELS[item] + ': ' + sel.pitchTitle);
          }
        }
      });

      if (parts.length === 0) {
        summaryEl.textContent = '';
        summaryEl.classList.remove('has-summary');
        return;
      }

      // Build a small stacked list. Each part on its own line.
      summaryEl.innerHTML = '';
      var ul = document.createElement('span');
      ul.className = 'bap-summary-list';
      parts.forEach(function (p) {
        var line = document.createElement('span');
        line.className = 'bap-summary-line';
        line.textContent = p;
        ul.appendChild(line);
      });
      summaryEl.appendChild(ul);
      summaryEl.classList.add('has-summary');
    }

    function renderAll() {
      renderRows();
      renderMethods();
      renderPitchSelection();
      renderTeamSelection();
      renderSummary();
      writeHiddenInputs();
    }

    /* ---------- Hidden inputs ---------- */

    function writeHiddenInputs() {
      var setVal = function (name, val) {
        var input = pickerEl.querySelector('input[name="' + name + '"]');
        if (input) input.value = val == null ? '' : String(val);
      };
      setVal('bap-resolved-decoration-type', state.choice);

      // BOOST CUSTOMISATION 2026-05-21 (v2.3.0): the resolved-method and
      // resolved-pitch hidden inputs now carry COMMA-SEPARATED lists of
      // all selected methods and pitch IDs, in canonical order. Kept for
      // any downstream reader; the cart-add path builds from state
      // directly, not these inputs.
      var selMethods = [];
      var selPitchIds = [];
      var selPitchTitles = [];
      METHOD_ORDER.forEach(function (m) {
        var sel = state.methods[m];
        if (sel && sel.pitchId) {
          selMethods.push(m);
          selPitchIds.push(sel.pitchId);
          selPitchTitles.push(sel.pitchTitle);
        }
      });
      setVal('bap-resolved-decoration-method', selMethods.join(','));
      setVal('bap-resolved-decoration-pitch-id', selPitchIds.join(','));
      setVal('bap-resolved-decoration-pitch-title', selPitchTitles.join('|'));

      var teamAdditions = TEAM_ORDER.filter(function (k) {
        return state.team[k] && state.team[k].variantId;
      }).map(function (k) {
        return k + ':' + state.team[k].variantId;
      }).join(',');
      setVal('bap-resolved-team-additions', teamAdditions);
    }

    /* ---------- Event wiring ---------- */

    // Top-level choice rows (button clicks)
    $$('[data-bap-choice-toggle]', pickerEl).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var choice = btn.getAttribute('data-bap-choice-toggle');
        var row = btn.closest('.bap-row');
        var hasBody = row && !!row.querySelector('.bap-row-content');

        if (state.choice === choice) {
          if (hasBody) state.isOpen = !state.isOpen;
        } else {
          state.choice = choice;
          state.isOpen = hasBody;
          // BOOST CUSTOMISATION 2026-05-21 (v2.3.0): leaving add_artwork
          // clears ALL method selections, the open accordion, and team.
          if (state.choice !== 'add_artwork') {
            state.openMethod = null;
            METHOD_ORDER.forEach(function (m) { state.methods[m] = null; });
            TEAM_ORDER.forEach(function (t) { state.team[t] = null; });
          }
        }

        renderAll();
        if (productId) saveState(productId, state);
      });
    });

    // Method headers (button clicks — accordion open/close ONLY).
    // BOOST CUSTOMISATION 2026-05-21 (v2.3.0): clicking a method header
    // toggles ONLY the open accordion (openMethod). It NEVER clears or
    // sets a selection. Single-expansion: opening one closes the others,
    // but their selections persist.
    $$('[data-bap-method-toggle]', pickerEl).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var method = btn.getAttribute('data-bap-method-toggle');
        if (state.openMethod === method) {
          state.openMethod = null;   // re-click collapses
        } else {
          state.openMethod = method; // open this, close others
        }
        renderMethods();             // only accordion visuals change
        if (productId) saveState(productId, state);
      });
    });

    // Pitch radios (change events).
    // BOOST CUSTOMISATION 2026-05-21 (v2.3.0): a pitch sets ONLY its own
    // method's selection. Selecting Embroidery never touches Screen Print.
    // The "None" radio (empty value) CLEARS that method's selection, the
    // same way the team "None" radios clear their slot.
    $$('input[data-bap-pitch="true"]', pickerEl).forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        var methodEl = input.closest('[data-method]');
        if (!methodEl) return;
        var method = methodEl.getAttribute('data-method');
        if (!method || !state.methods.hasOwnProperty(method)) return;

        if (!input.value) {
          // "None" selected for this method — clear its selection.
          state.methods[method] = null;
        } else {
          state.methods[method] = {
            pitchId: parseInt(input.value, 10),
            pitchTitle: input.getAttribute('data-pitch-title') || '',
            pitchHandle: input.getAttribute('data-pitch-handle') || '',
            pitchPrice: parseInt(input.getAttribute('data-pitch-price') || '0', 10),
            // BOOST CUSTOMISATION 2026-07-02 (WS2): production setup-fee handle for
            // this pitch (data-pitch-fee), used to inject the correct method fee on
            // add-to-cart and written onto the decoration line as _setup_fee.
            pitchFee: input.getAttribute('data-pitch-fee') || ''
          };
        }
        renderAll();
        if (productId) saveState(productId, state);
      });
    });

    // Team radios (BOOST CUSTOMISATION 2026-06-03 v2.4.0: now nested inside the
    // Names and Numbers method body).
    // BOOST CUSTOMISATION 2026-05-21 (v2.3.0): unchanged in logic — each
    // slot is independent — but the markup moved into Heat Press. Choosing
    // a team item must also refresh the Names and Numbers header selected-state,
    // so we call renderMethods() in addition to renderTeamSelection().
    if (refs.renderTeamPane) {
      $$('[data-team-method]', pickerEl).forEach(function (input) {
        input.addEventListener('change', function () {
          if (!input.checked) return;
          var key = input.getAttribute('data-team-method');
          if (!input.value) {
            state.team[key] = null;
          } else {
            state.team[key] = {
              variantId: parseInt(input.value, 10),
              title: input.getAttribute('data-pitch-title') || '',
              handle: input.getAttribute('data-pitch-handle') || '',
              price: parseInt(input.getAttribute('data-pitch-price') || '0', 10)
            };
          }
          renderTeamSelection();
          renderMethods();    // Names and Numbers header may now be "selected"
          renderSummary();
          writeHiddenInputs();
          if (productId) saveState(productId, state);
        });
      });
    }

    /* ---------- AJAX add-to-cart ---------- */

    function buildItemsForCartAdd(garmentVariantId, garmentQuantity, feeFlags) {
      // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2) — LINE ORDER.
      // Shopify sorts cart lines reverse-chronologically by add time
      // (newest at TOP), and there is no Ajax endpoint to reposition a
      // line. Within ONE /cart/add.js call the LAST array element is the
      // newest, so it floats highest. To make a group render top->bottom as
      //   Garment, decorations (rank 01..08), $70 fee, $50 fee
      // we build the items array in the REVERSE of that:
      //   [ $50 fee?, $70 fee?, decorations (reverse rank 08..01), garment ]
      // so the garment (array-last) floats to the group top, each lower-rank
      // decoration sits just under it, and the fees sink to the group bottom
      // ($70 above $50).
      //
      // FEE OWNERSHIP (v2.3.0 Stage 2): the PICKER is now the sole ADDER of
      // setup fees (so they ride this call and land at the group bottom). It
      // includes a fee ONLY when feeFlags says that fee is needed AND not
      // already in the cart (computed by doFreshAdd from a /cart.js read).
      // The fee-manager remains the sole REMOVER/ENFORCER (dedupe to 1,
      // remove when no decorated/embroidery groups remain). Single adder +
      // single remover avoids any add/observe/add race.
      feeFlags = feeFlags || { includeArtworkFee: false, methodFees: [] };

      // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): build the visible
      // "For garment" property value from the product title and the currently
      // selected colour and size options. Reads option selects from the
      // product form so it reflects the customer's live selection at add time.
      // Used on ALL carrier lines (decorations, sample-policy acks) so the
      // garment<->carrier relationship is legible at the Shopify hosted checkout
      // and in Order Printer Pro templates. Gracefully degrades to product title
      // alone if options are not found.
      var forGarmentLabel = (function () {
        var title = refs.productTitle || '';
        var form = pickerEl.closest('form');
        var colour = '';
        var size = '';
        if (form) {
          var opts = form.querySelectorAll('select[id*="option"], input[type="radio"][id*="option"]:checked');
          if (opts.length === 0) {
            // Fallback: read option selects without ID pattern
            opts = form.querySelectorAll('select[name*="option"]');
          }
          for (var oi = 0; oi < opts.length; oi++) {
            var el = opts[oi];
            var lbl = (el.getAttribute('aria-label') || el.getAttribute('data-option-name') || el.getAttribute('name') || '').toLowerCase();
            var val = el.value || '';
            if (!val) continue;
            if (lbl.indexOf('colour') !== -1 || lbl.indexOf('color') !== -1) colour = val;
            else if (lbl.indexOf('size') !== -1) size = val;
          }
        }
        if (colour && size) return title + ' (' + colour + ' / ' + size + ')';
        if (colour) return title + ' (' + colour + ')';
        if (size) return title + ' (' + size + ')';
        return title;
      })();

      var productKey = generateProductKey();
      var items = [];
      // BOOST CUSTOMISATION 2026-07-02 (WS2 fix, T-0702-313): sample-only still
      // needs a carrier group; "Add logo or artwork" needs one only when a
      // decoration is actually selected. An empty "Add logo or artwork" gets no
      // _product_key, so the fee manager does not read it as a decorated group.
      var needsCarriers = (state.choice === 'sample_only') || isEffectivelyDecorated();

      // ----- Fees first in the array (=> sink to group bottom) -----
      // Order within fees: push $50 first then $70, so $70 ends up ABOVE $50
      // (Artwork Setup Fee above Embroidery Setup Fee).
      if (state.choice === 'add_artwork') {
        // BOOST CUSTOMISATION 2026-07-02 (WS2): inject the required production
        // setup fees (one per distinct handle across the selected methods), then
        // the $70 Artwork fee above them. Fees are pushed before decorations so
        // they sink to the group bottom; artwork is pushed last of the fees so it
        // sits ABOVE the production fees. Each fee line carries _fee_type (so the
        // themed cart excludes it from the item count and skips it in the normal
        // line loop) and, for production fees, _setup_fee (its handle).
        if (feeFlags.methodFees && feeFlags.methodFees.length) {
          for (var mf = feeFlags.methodFees.length - 1; mf >= 0; mf--) {
            items.push({
              id: feeFlags.methodFees[mf].variantId,
              quantity: 1,
              properties: { _fee_type: 'setup', _setup_fee: feeFlags.methodFees[mf].handle }
            });
          }
        }
        if (feeFlags.includeArtworkFee && feeFlags.artworkFeeVariantId) {
          items.push({
            id: feeFlags.artworkFeeVariantId,
            quantity: 1,
            properties: { _fee_type: 'artwork_setup' }
          });
        }
      }

      // ----- Decorations in REVERSE rank (=> render in rank order under garment) -----
      // Build the selected decorations in canonical (rank) order first,
      // then push them reversed so rank-01 ends up nearest the garment.
      if (state.choice === 'add_artwork') {
        var orderedDecos = [];
        METHOD_ORDER.forEach(function (method) {
          var sel = state.methods[method];
          if (!sel || !sel.pitchId) return;
          orderedDecos.push({
            id: sel.pitchId,
            properties: {
              _product_key: productKey,
              _product_option: 'decoration',
              _decoration_method: method,
              // BOOST CUSTOMISATION 2026-07-02 (WS2): production setup-fee handle
              // for this method (may be empty); copied onto the cart line below.
              _setup_fee: (sel.pitchFee || '')
            }
          });
        });
        if (refs.renderTeamPane) {
          TEAM_ORDER.forEach(function (slot) {
            var pick = state.team[slot];
            if (!pick) return;
            orderedDecos.push({
              id: pick.variantId,
              properties: {
                _product_key: productKey,
                _product_option: 'decoration',
                _decoration_method: slot
              }
            });
          });
        }
        // Push reversed: array-later = higher in cart, so the last-ranked
        // decoration goes in first (sinks lowest of the decos), rank-01 goes
        // in last (rises just under the garment).
        for (var d = orderedDecos.length - 1; d >= 0; d--) {
          var decoProps = {
            _product_key: productKey,
            _product_option: 'decoration',
            _decoration_method: orderedDecos[d].properties._decoration_method
          };
          // BOOST CUSTOMISATION 2026-07-02 (WS2): carry the production setup-fee
          // handle onto the decoration line so boost-cart-fee-manager.js can
          // enforce/dedupe/remove it once per order per fee. Absent on team slots.
          if (orderedDecos[d].properties._setup_fee) {
            decoProps._setup_fee = orderedDecos[d].properties._setup_fee;
          }
          // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): visible "For garment"
          // property — no underscore so Shopify renders it at checkout and in
          // Order Printer Pro. Themed cart suppresses the raw property row
          // (carriers are filtered from the themed cart render entirely).
          if (forGarmentLabel) decoProps['For garment'] = forGarmentLabel;
          items.push({
            id: orderedDecos[d].id,
            quantity: garmentQuantity,
            properties: decoProps
          });
        }
      }

      // ----- Sample-policy carrier — sample-only flow -----
      // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2):
      // Two acknowledgement carriers are now supported:
      //   - AS Colour (mandatory-policy): data-as-colour-sample-id
      //   - Generic (all other sample-only adds): data-generic-sample-id
      // For AS Colour: write the acknowledgement only when samplePolicyMandatory
      // is true (i.e. the product carries the 'mandatory' sample_policy metafield).
      // For non-AS-Colour sample-only adds (samplePolicyMandatory is false):
      // write the generic acknowledgement if the product id resolves.
      // Both carriers are qty 1, pushed BEFORE the garment so the garment floats
      // to the group top under Shopify LIFO. Both get the "For garment" property.
      if (state.choice === 'sample_only') {
        var ackVariantId = null;
        var ackProps = {};
        if (refs.samplePolicyMandatory && refs.asColourSampleId) {
          ackVariantId = parseInt(refs.asColourSampleId, 10);
          ackProps = {
            _product_key: productKey,
            _product_option: 'sample_confirmation',
            _sample_vendor: refs.vendor
          };
        } else if (!refs.samplePolicyMandatory && refs.genericSampleId) {
          ackVariantId = parseInt(refs.genericSampleId, 10);
          ackProps = {
            _product_key: productKey,
            _product_option: 'sample_confirmation'
          };
        }
        if (ackVariantId && !isNaN(ackVariantId) && ackVariantId > 0) {
          if (forGarmentLabel) ackProps['For garment'] = forGarmentLabel;
          items.push({
            id: ackVariantId,
            quantity: 1,
            properties: ackProps
          });
        }
      }

      // ----- Garment LAST (=> floats to group top) -----
      var garmentProps = {};
      if (needsCarriers) {
        garmentProps._product_key = productKey;
        if (state.choice === 'sample_only') {
          garmentProps._sample_only = 'true';
        }
      }
      items.push({
        id: garmentVariantId,
        quantity: garmentQuantity,
        properties: garmentProps
      });

      return { items: items, productKey: productKey };
    }

    /* ============================================
    FEE HANDLING (v2.3.0 Stage 2): ownership split so fees can be pinned to
    the group bottom (see buildItemsForCartAdd). The PICKER adds fees inside
    its own /cart/add.js call (first in the array => group bottom under LIFO),
    but ONLY a fee that is not already in the cart. The fee manager
    (boost-cart-fee-manager.js) no longer ADDS fees; it REMOVES/ENFORCES
    (dedupe to qty 1, remove when no decorated/embroidery groups remain) and
    syncs decoration quantities. Single adder + single remover = no race.
    ============================================ */

    // Resolve fee variant IDs from the global config the fee-manager uses.
    function getFeeConfig() {
      var cfg = (typeof window !== 'undefined' && window.BoostFeeConfig) ? window.BoostFeeConfig : {};
      var artwork = Number(cfg.feeVariantId);
      var emb = Number(cfg.embroideryFeeVariantId);
      // BOOST CUSTOMISATION 2026-07-02 (WS2): the full handle -> variantId map is
      // the single source of truth for every setup fee. artworkFeeVariantId and
      // embroideryFeeVariantId are retained for the legacy paths.
      var map = (cfg.feeVariants && typeof cfg.feeVariants === 'object') ? cfg.feeVariants : {};
      return {
        artworkFeeVariantId: (!isNaN(artwork) && artwork > 0) ? artwork : null,
        embroideryFeeVariantId: (!isNaN(emb) && emb > 0) ? emb : null,
        feeVariants: map
      };
    }

    // Does this add include any embroidery decoration?
    function addIncludesEmbroidery() {
      return !!(state.methods.embroidery && state.methods.embroidery.pitchId);
    }

    // BOOST CUSTOMISATION 2026-07-02 (WS2): distinct production setup fees required
    // by the current selection. Reads each selected method's data-pitch-fee handle
    // (captured on state as pitchFee), resolves it to a live variant id via
    // feeVariants, and de-dupes by handle so a fee shared across methods (e.g. the
    // Standard DTF fee for both Plastisol and Power Print) resolves once.
    function requiredMethodFees(feeCfg) {
      var byHandle = {};
      METHOD_ORDER.forEach(function (m) {
        var sel = state.methods[m];
        if (sel && sel.pitchId && sel.pitchFee) byHandle[sel.pitchFee] = true;
      });
      var out = [];
      Object.keys(byHandle).forEach(function (h) {
        var id = Number(feeCfg.feeVariants ? feeCfg.feeVariants[h] : 0);
        if (!isNaN(id) && id > 0) out.push({ handle: h, variantId: id });
      });
      return out;
    }

    // From a /cart.js snapshot, is a given fee variant already present?
    function cartHasFee(cart, feeVariantId) {
      if (!feeVariantId || !cart || !cart.items) return false;
      return cart.items.some(function (it) {
        return Number(it.variant_id) === feeVariantId && it.quantity > 0;
      });
    }

    /* ---------- Cart drawer helper ---------- */

    function openCartDrawer(response) {
      if (typeof ajaxCart !== 'undefined' && typeof ajaxCart.load === 'function') {
        ajaxCart.load(response);
      } else if (response && response.sections && response.sections['side-cart']) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(response.sections['side-cart'], 'text/html');
        var sideCart = document.getElementById('cart');
        if (sideCart) {
          var fresh = doc.getElementById('cart');
          if (fresh) sideCart.innerHTML = fresh.innerHTML;
        }
      }
    }

    /* ---------- Merge signature ----------
     *
     * BOOST CUSTOMISATION 2026-05-21 (v2.3.0):
     * Merge-into-existing-group is now allowed ONLY for the simplest case:
     * a garment with EXACTLY ONE artwork method and NO team items. Any
     * multi-method or any team selection forces a fresh add. This mirrors
     * the pre-2.3.0 team behaviour (team always forced fresh add) and
     * avoids fragile signature matching across multiple decoration lines.
     */

    function buildConfigSignature(garmentVariantId) {
      if (state.choice === 'no_artwork') {
        return null;
      }
      if (state.choice === 'sample_only') {
        return String(garmentVariantId) + '|sample';
      }
      if (state.choice === 'add_artwork') {
        // No merge if any team item is selected.
        if (anyTeamSelected()) return null;
        // No merge unless EXACTLY one method is selected.
        if (selectedMethodCount() !== 1) return null;
        // Find the single selected method's pitch id.
        var theMethod = null;
        METHOD_ORDER.forEach(function (m) {
          if (state.methods[m] && state.methods[m].pitchId) theMethod = m;
        });
        if (!theMethod) return null;
        return String(garmentVariantId) + '|' + String(state.methods[theMethod].pitchId);
      }
      return null;
    }

    // Derive the aggregation key from an existing cart group.
    // Returns null if the group is incomplete, multi-method, or uses
    // team-decoration (all of which are no-merge in v2.3.0).
    function derivePkSignature(cart, pk) {
      var garment = null;
      var legacyTracker = null;
      var decorationItemLines = [];
      var hasTeamCarrier = false;

      cart.items.forEach(function (item) {
        var p = item.properties || {};
        if (p._product_key !== pk) return;
        if (p._hidden_in_cart === 'true' || p._hidden_in_cart === true) {
          legacyTracker = item;
        } else if (p._product_option === 'decoration') {
          var teamSlotNames = ['front_number', 'back_number', 'team_name', 'player_name'];
          var m = p._decoration_method || '';
          if (teamSlotNames.indexOf(m) !== -1) {
            hasTeamCarrier = true;
          } else {
            decorationItemLines.push(item);
          }
        } else if (p._product_option === 'sample_confirmation') {
          // Sample policy carrier — doesn't affect signature
        } else if (!p._product_option) {
          garment = item;
        }
      });

      if (!garment) return null;
      if (hasTeamCarrier) return null;                 // team: no merge
      if (decorationItemLines.length > 1) return null; // multi-method: no merge

      var garmentSampleFlag = garment.properties && garment.properties._sample_only;
      var legacyTrackerSampleFlag = legacyTracker && legacyTracker.properties && legacyTracker.properties._sample_only;
      var isSampleOnly = (garmentSampleFlag === 'true' || garmentSampleFlag === true)
                      || (legacyTrackerSampleFlag === 'true' || legacyTrackerSampleFlag === true);

      if (isSampleOnly) {
        return String(garment.variant_id) + '|sample';
      }

      if (decorationItemLines.length === 1) {
        return String(garment.variant_id) + '|' + String(decorationItemLines[0].variant_id);
      }

      return null;
    }

    function findMatchingGroup(cart, targetSig) {
      if (!targetSig) return null;
      var pksSeen = {};
      cart.items.forEach(function (item) {
        var p = item.properties || {};
        if (p._product_key) pksSeen[p._product_key] = true;
      });
      var matchedPk = null;
      Object.keys(pksSeen).some(function (pk) {
        if (derivePkSignature(cart, pk) === targetSig) {
          matchedPk = pk;
          return true;
        }
        return false;
      });
      if (!matchedPk) return null;
      var allIndices = [];
      cart.items.forEach(function (item, idx) {
        if ((item.properties || {})._product_key === matchedPk) {
          allIndices.push(idx);
        }
      });
      return { pk: matchedPk, allIndices: allIndices };
    }

    function mergeIntoExistingGroup(cart, match, garmentQuantity) {
      var updates = new Array(cart.items.length);
      for (var i = 0; i < cart.items.length; i++) {
        updates[i] = cart.items[i].quantity;
      }
      match.allIndices.forEach(function (idx) {
        updates[idx] = updates[idx] + garmentQuantity;
      });
      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ updates: updates, sections: 'side-cart' })
      }).then(function (r) {
        if (!r.ok) throw new Error('Cart merge update failed.');
        return r.json();
      });
    }

    function addToCart(garmentVariantId, garmentQuantity) {
      // BOOST CUSTOMISATION 2026-07-02 (WS2 fix, T-0702-313): an "Add logo or
      // artwork" choice with no method or team selected is no longer blocked. It
      // adds the garment undecorated (no carriers, no _product_key, no fees),
      // handled via isEffectivelyDecorated() in the carrier build and fee logic.
      var task = submissionChain.catch(function () { /* swallow */ })
        .then(function () { return runAddToCart(garmentVariantId, garmentQuantity); });
      submissionChain = task;
      return task;
    }

    function runAddToCart(garmentVariantId, garmentQuantity) {
      var targetSig = buildConfigSignature(garmentVariantId);
      if (!targetSig) {
        return doFreshAdd(garmentVariantId, garmentQuantity);
      }

      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var match = findMatchingGroup(cart, targetSig);
          if (match) {
            return mergeIntoExistingGroup(cart, match, garmentQuantity)
              .then(function (response) {
                openCartDrawer(response);
                document.dispatchEvent(new CustomEvent('boost:cart-updated', {
                  detail: { source: 'artwork-picker-merge', productKey: match.pk }
                }));
              });
          }
          return doFreshAdd(garmentVariantId, garmentQuantity);
        });
    }

    function doFreshAdd(garmentVariantId, garmentQuantity) {
      // BOOST CUSTOMISATION 2026-07-02 (WS2):
      // Read the cart first to decide which fees to include in THIS add (so
      // they ride the call and land at the group bottom). Include each fee only
      // when it is needed for this add AND not already present in the cart
      // (once per order per fee). The fee-manager enforces/removes; it never adds.
      var feeCfg = getFeeConfig();
      var isDecorated = isEffectivelyDecorated();
      var methodFeesAll = isDecorated ? requiredMethodFees(feeCfg) : [];

      // If this isn't a decorated add, or there are no fees to add at all,
      // skip the cart read and build with no fees.
      if (!isDecorated || (!feeCfg.artworkFeeVariantId && methodFeesAll.length === 0)) {
        return performFreshAdd(garmentVariantId, garmentQuantity, {
          includeArtworkFee: false,
          methodFees: []
        });
      }

      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var feeFlags = {
            artworkFeeVariantId: feeCfg.artworkFeeVariantId,
            // $70 Artwork needed for any decorated add, if not already present.
            includeArtworkFee: !!feeCfg.artworkFeeVariantId
              && !cartHasFee(cart, feeCfg.artworkFeeVariantId),
            // Production fees required by this selection, minus any already present.
            methodFees: methodFeesAll.filter(function (f) {
              return !cartHasFee(cart, f.variantId);
            })
          };
          return performFreshAdd(garmentVariantId, garmentQuantity, feeFlags);
        })
        .catch(function () {
          // If the cart read fails, fall back to adding WITHOUT fees and let
          // the next decorated add self-correct (degraded ordering, but never a
          // missing/duplicate fee, since the manager never adds).
          return performFreshAdd(garmentVariantId, garmentQuantity, {
            includeArtworkFee: false,
            methodFees: []
          });
        });
    }

    function performFreshAdd(garmentVariantId, garmentQuantity, feeFlags) {
      var built = buildItemsForCartAdd(garmentVariantId, garmentQuantity, feeFlags);

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: built.items, sections: 'side-cart' })
      })
        .then(function (r) {
          if (!r.ok) {
            return r.text().then(function (text) {
              var msg = 'Add to cart failed. Please try again.';
              try { var parsed = JSON.parse(text); msg = parsed.description || parsed.message || msg; } catch (e) {}
              throw new Error(msg);
            });
          }
          return r.json();
        })
        .then(function (response) {
          openCartDrawer(response);
          document.dispatchEvent(new CustomEvent('boost:cart-updated', {
            detail: { source: 'artwork-picker', productKey: built.productKey }
          }));
        });
    }

    pickerEl.bapAddToCart = addToCart;

    /* ---------- Buy-button intercept ---------- */

    var form = pickerEl.closest('form');
    if (form) {
      form.addEventListener('submit', function (e) {
        if (pickerEl.offsetParent === null) return;
        e.preventDefault();
        e.stopPropagation();

        var variantInput = form.querySelector('input[name="id"], input[name="variant-id"], select[name="variant-id"]');
        var qtyInput = form.querySelector('input[name="quantity"]');
        var variantId = variantInput ? parseInt(variantInput.value, 10) : null;
        var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        if (!variantId) {
          showError(pickerEl, 'Please choose all options before adding to cart.');
          return false;
        }

        var btn = form.querySelector('button[type="submit"]');
        if (btn) btn.classList.add('processing');

        addToCart(variantId, quantity)
          .then(function () {
            if (btn) btn.classList.remove('processing');
          })
          .catch(function (err) {
            if (btn) btn.classList.remove('processing');
            showError(pickerEl, err && err.message ? err.message : 'Something went wrong. Please try again.');
          });

        return false;
      }, true);
    }

    // Initial render
    renderAll();
  }

  /* ---------- Error surfacing ---------- */

  function showError(pickerEl, msg) {
    var err = pickerEl.querySelector('.bap-error');
    if (!err) {
      err = document.createElement('div');
      err.className = 'bap-error';
      err.setAttribute('role', 'alert');
      pickerEl.appendChild(err);
    }
    err.textContent = msg;
    err.classList.add('is-visible');
    clearTimeout(err.__hideTimer);
    err.__hideTimer = setTimeout(function () {
      err.classList.remove('is-visible');
    }, 5000);
  }

  /* ---------- Bootstrap ---------- */

  function init() {
    Array.prototype.slice.call(document.querySelectorAll('.boost-artwork-picker')).forEach(initPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after the theme replaces DOM on variant change.
  window.addEventListener('productVariants', function () {
    setTimeout(init, 50);
  });

})();
